import { defineMessages, type MessageDescriptor } from 'react-intl';

type InferredProjectsMessages = {
	following: MessageDescriptor;
	livingSummary: MessageDescriptor;
	timeline: MessageDescriptor;
	recentDocuments: MessageDescriptor;
	workstreamSummary: MessageDescriptor;
	decisions: MessageDescriptor;
	decisionTitle: MessageDescriptor;
	decisionStatus: MessageDescriptor;
	decisionDate: MessageDescriptor;
	decisionSources: MessageDescriptor;
	owner: MessageDescriptor;
	contributors: MessageDescriptor;
	goals: MessageDescriptor;
	contributesTo: MessageDescriptor;
	unassignedOwner: MessageDescriptor;
	showMoreContributors: MessageDescriptor;
	showLessContributors: MessageDescriptor;
	newTimelineEntry: MessageDescriptor;
	showMore: MessageDescriptor;
	showLess: MessageDescriptor;
	noRecommendedGoals: MessageDescriptor;
	summaryError: MessageDescriptor;
	graphClaw: MessageDescriptor;
	graphClawToggle: MessageDescriptor;
	graphClawEnabled: MessageDescriptor;
	graphClawDisabled: MessageDescriptor;
	graphClawDebug: MessageDescriptor;
	graphClawLiveData: MessageDescriptor;
	graphClawLiveDataToggle: MessageDescriptor;
	graphClawForceRefresh: MessageDescriptor;
	graphClawForceRefreshAction: MessageDescriptor;
	graphClawForceRefreshToggle: MessageDescriptor;
	graphClawWaiting: MessageDescriptor;
	graphClawWaitingToggle: MessageDescriptor;
	activateGraphClaw: MessageDescriptor;
	activateGraphClawDescription: MessageDescriptor;
	activateGraphClawButton: MessageDescriptor;
	weeklyUpdateTitle: MessageDescriptor;
	loadingInferredProjects: MessageDescriptor;
	unableToLoadInferredProjects: MessageDescriptor;
	unknownInferredProjectsError: MessageDescriptor;
	noInferredProjectsReturned: MessageDescriptor;
	citations: MessageDescriptor;
	showCitations: MessageDescriptor;
	showCitationsForContext: MessageDescriptor;
};

export const messages: InferredProjectsMessages = defineMessages({
	following: {
		id: 'dtac.inferredProjects.following',
		defaultMessage: 'Inferred Projects',
		description: 'Heading for the in-page sidebar listing inferred projects.',
	},
	livingSummary: {
		id: 'dtac.inferredProjects.livingSummary',
		defaultMessage: 'Living Summary',
		description: 'Heading for the collapsible summary section on the Inferred Projects page.',
	},
	timeline: {
		id: 'dtac.inferredProjects.timeline',
		defaultMessage: 'Timeline',
		description: 'Heading for the timeline section on the Inferred Projects page.',
	},
	recentDocuments: {
		id: 'dtac.inferredProjects.recentDocuments',
		defaultMessage: 'Recent documents',
		description: 'Heading for recent document cards on the Inferred Projects page.',
	},
	workstreamSummary: {
		id: 'dtac.inferredProjects.workstreamSummary',
		defaultMessage: 'Workstream summary',
		description: 'Heading for the generated workstream summary list in the living summary section.',
	},
	decisions: {
		id: 'dtac.inferredProjects.decisions',
		defaultMessage: 'Decisions',
		description: 'Heading for the generated decision table in the living summary section.',
	},
	decisionTitle: {
		id: 'dtac.inferredProjects.decisionTitle',
		defaultMessage: 'Title',
		description: 'Column heading for the title of a generated decision.',
	},
	decisionStatus: {
		id: 'dtac.inferredProjects.decisionStatus',
		defaultMessage: 'Status',
		description: 'Column heading for the status of a generated decision.',
	},
	decisionDate: {
		id: 'dtac.inferredProjects.decisionDate',
		defaultMessage: 'Date',
		description: 'Column heading for the date of a generated decision.',
	},
	decisionSources: {
		id: 'dtac.inferredProjects.decisionSources',
		defaultMessage: 'Sources',
		description: 'Column heading for source citations in the generated decision table.',
	},
	owner: {
		id: 'dtac.inferredProjects.owner',
		defaultMessage: 'Owner',
		description: 'Heading for the owner card on the Inferred Projects page.',
	},
	contributors: {
		id: 'dtac.inferredProjects.contributors',
		defaultMessage: 'Contributors',
		description: 'Heading for the contributors card on the Inferred Projects page.',
	},
	goals: {
		id: 'dtac.inferredProjects.goals',
		defaultMessage: 'Goals',
		description: 'Heading for the goals card on the Inferred Projects page.',
	},
	contributesTo: {
		id: 'dtac.inferredProjects.contributesTo',
		defaultMessage: 'Contributes to',
		description: 'Heading for the right rail card listing goals this project contributes to.',
	},
	unassignedOwner: {
		id: 'dtac.inferredProjects.unassignedOwner',
		defaultMessage: 'Unassigned',
		description: 'Fallback label shown when an inferred project does not have an owner.',
	},
	showMoreContributors: {
		id: 'dtac.inferredProjects.showMoreContributors',
		defaultMessage: 'Show more contributors',
		description: 'Accessible label for the button that expands the contributor list.',
	},
	showLessContributors: {
		id: 'dtac.inferredProjects.showLessContributors',
		defaultMessage: 'Show less contributors',
		description: 'Accessible label for the button that collapses the contributor list.',
	},
	newTimelineEntry: {
		id: 'dtac.inferredProjects.newTimelineEntry',
		defaultMessage: 'New',
		description: 'Status label shown on the newest timeline entry.',
	},
	showMore: {
		id: 'dtac.inferredProjects.showMore',
		defaultMessage: 'Show more',
		description: 'Button label to show more recent documents.',
	},
	showLess: {
		id: 'dtac.inferredProjects.showLess',
		defaultMessage: 'Show less',
		description: 'Button label to show fewer recent documents.',
	},
	noRecommendedGoals: {
		id: 'dtac.inferredProjects.noRecommendedGoals',
		defaultMessage: 'No recommended goals yet',
		description:
			'Empty state shown when Rovo has finished ranking goals and found no recommendations.',
	},
	summaryError: {
		id: 'dtac.inferredProjects.summaryError',
		defaultMessage: 'Summary could not be generated.',
		description: 'Inline error text shown when the living summary fails to generate.',
	},
	graphClaw: {
		id: 'dtac.inferredProjects.graphClaw',
		defaultMessage: 'GraphClaw',
		description: 'Name of the floating GraphClaw preview control.',
	},
	graphClawToggle: {
		id: 'dtac.inferredProjects.graphClawToggle',
		defaultMessage: 'GraphClaw status',
		description: 'Accessible label for the floating GraphClaw enabled or disabled control.',
	},
	graphClawEnabled: {
		id: 'dtac.inferredProjects.graphClawEnabled',
		defaultMessage: 'Enabled',
		description: 'Button label indicating GraphClaw is enabled.',
	},
	graphClawDisabled: {
		id: 'dtac.inferredProjects.graphClawDisabled',
		defaultMessage: 'Disabled',
		description: 'Button label indicating GraphClaw is disabled.',
	},
	graphClawDebug: {
		id: 'dtac.inferredProjects.graphClawDebug',
		defaultMessage: 'Debug Settings',
		description: 'Section label for GraphClaw debug controls.',
	},
	graphClawLiveData: {
		id: 'dtac.inferredProjects.graphClawLiveData',
		defaultMessage: 'Live data',
		description: 'Label for a GraphClaw debug control that switches to live inferred project data.',
	},
	graphClawLiveDataToggle: {
		id: 'dtac.inferredProjects.graphClawLiveDataToggle',
		defaultMessage: 'Use live inferred projects data',
		description: 'Accessible label for the GraphClaw debug switch that toggles live data.',
	},
	graphClawForceRefresh: {
		id: 'dtac.inferredProjects.graphClawForceRefresh',
		defaultMessage: 'Force refresh',
		description: 'Label for a GraphClaw control that refreshes inferred project data.',
	},
	graphClawForceRefreshAction: {
		id: 'dtac.inferredProjects.graphClawForceRefreshAction',
		defaultMessage: 'Run',
		description: 'Button label that starts a GraphClaw force refresh.',
	},
	graphClawForceRefreshToggle: {
		id: 'dtac.inferredProjects.graphClawForceRefreshToggle',
		defaultMessage: 'Force refresh GraphClaw data',
		description: 'Accessible label for the button that refreshes GraphClaw data.',
	},
	graphClawWaiting: {
		id: 'dtac.inferredProjects.graphClawWaiting',
		defaultMessage: 'Waiting',
		description: 'Label for the GraphClaw preview control that simulates generated fields waiting.',
	},
	graphClawWaitingToggle: {
		id: 'dtac.inferredProjects.graphClawWaitingToggle',
		defaultMessage: 'Simulate inferred item loading',
		description: 'Accessible label for the GraphClaw preview loading simulation toggle.',
	},
	activateGraphClaw: {
		id: 'dtac.inferredProjects.activateGraphClaw',
		defaultMessage: 'Activate GraphClaw to turbo charge your Teamwork Graph',
		description:
			'Empty state message shown in smart Inferred Projects sections when GraphClaw is disabled.',
	},
	activateGraphClawDescription: {
		id: 'dtac.inferredProjects.activateGraphClawDescription',
		defaultMessage:
			'Power this project with GraphClaw to pull together all the relevant context and work from your team. Get the right context and updates, at the right time, automagically',
		description:
			'Supporting copy shown in the GraphClaw activation panel on the Inferred Projects page.',
	},
	activateGraphClawButton: {
		id: 'dtac.inferredProjects.activateGraphClawButton',
		defaultMessage: 'Activate GraphClaw',
		description: 'Button label that enables GraphClaw from the Inferred Projects activation panel.',
	},
	weeklyUpdateTitle: {
		id: 'dtac.inferredProjects.weeklyUpdateTitle',
		defaultMessage: 'Weekly update',
		description: 'Title shown in the header of the weekly progress update card.',
	},
	loadingInferredProjects: {
		id: 'dtac.inferredProjects.loadingInferredProjects',
		defaultMessage: 'Loading inferred projects',
		description: 'Accessible label for the loading state on the live Inferred Projects page.',
	},
	unableToLoadInferredProjects: {
		id: 'dtac.inferredProjects.unableToLoadInferredProjects',
		defaultMessage: 'Unable to load inferred projects: {error}',
		description: 'Error message shown when the live Inferred Projects request fails.',
	},
	unknownInferredProjectsError: {
		id: 'dtac.inferredProjects.unknownInferredProjectsError',
		defaultMessage: 'Unknown error',
		description: 'Fallback error detail shown when the live Inferred Projects request fails.',
	},
	noInferredProjectsReturned: {
		id: 'dtac.inferredProjects.noInferredProjectsReturned',
		defaultMessage: 'No inferred projects were returned.',
		description:
			'Information message shown when the live Inferred Projects request returns no projects.',
	},
	citations: {
		id: 'dtac.inferredProjects.citations',
		defaultMessage: 'Citations',
		description: 'Accessible label for the citations list in the Inferred Projects summary popup.',
	},
	showCitations: {
		id: 'dtac.inferredProjects.showCitations',
		defaultMessage: 'Show citations: {triggerLabel}',
		description: 'Accessible label for the button that opens citations for a summary section.',
	},
	showCitationsForContext: {
		id: 'dtac.inferredProjects.showCitationsForContext',
		defaultMessage: 'Show citations for {context}: {triggerLabel}',
		description:
			'Accessible label for the button that opens citations for a specific generated summary row.',
	},
});
